import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import Header from "../components/UserNavebar";
import Footer from "../components/Footer";
import avatarImg from "../assets/Images/avatar.jpg";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ProfileDetailPage = () => {
  const [profile, setProfile] = useState(null);
  const [searchType, setSearchType] = useState("basic"); // default

  useEffect(() => {
    const savedProfile = localStorage.getItem("selectedUserData");
    const search = localStorage.getItem("search"); // 👈 search type (basic / advance)
    if (savedProfile) {
      setProfile(JSON.parse(savedProfile));
    }
    if (search) {
      setSearchType(search);
    }
  }, []);

  const handleSavePDF = () => {
    const button = document.getElementById("pdf-button");
    button.style.visibility = "hidden"; 
  
    const element = document.getElementById("profile-pdf");
    if (!element) return;
  
    html2canvas(element, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
      let heightLeft = imgHeight;
      let position = 0;
  
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
  
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
  
      pdf.save(`${profile.name}_profile.pdf`);
      button.style.visibility = "visible"; 
    });
  };

  if (!profile) {
    return (
      <>
        <Header />
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography variant="h6">No profile data found.</Typography>
        </Box>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Box
        sx={{
          bgcolor: "#f0f0f0",
          minHeight: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: { xs: 3, md: 6 },
        }}
      >
        <Paper
          id="profile-pdf"
          elevation={3}
          sx={{
            maxWidth: 1000,
            width: "100%",
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            backgroundColor: "white",
          }}
        >
          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              mb: 1,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            {profile.name}
          </Typography>
          
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: "center",
              mb: { xs: 3, md: 4 },
              color: "gray",
              fontSize: { xs: "0.9rem", md: "1rem" },
            }}
          >
            The summary of the general search is found below.
          </Typography>

          {/* Main Flex: Avatar + Details */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "flex-start",
              gap: { xs: 3, md: 5 },
            }}
          >
            {/* Avatar */}
            <Box sx={{ textAlign: "center", flex: "0 0 200px" }}>
              <Box
                component="img"
                src={avatarImg}
                alt={profile.name}
                sx={{
                  width: 150,
                  height: 150,
                  borderRadius: "50%",
                  objectFit: "cover",
                  mb: 1,
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {profile.name}
              </Typography>
              <Button
    id="pdf-button"
    variant="contained"
    onClick={handleSavePDF}
    sx={{
      backgroundColor: "#a7c957",
      color: "black",
      fontWeight: "bold",
      textTransform: "none",
      marginTop: 2,      
      "&:hover": { backgroundColor: "#8bbf40" },
    }}
  >
    Save as PDF
  </Button>
            </Box>

            {/* Right Side (Basic + Advance) */}
            <Box sx={{ flex: 1, lineHeight: 2 }}>
              {/* BASIC PROFILE */}
              <Box
                sx={{
                  borderBottom: "3px solid black",
                  mb: 2,
                  pb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", fontSize: "1.2rem" }}
                >
                  BASIC PROFILE
                </Typography>
              </Box>
              {[
                { label: "Date of Birth", value: profile.dob },
                { label: "Gender", value: profile.gender },
                { label: "Post Code", value: profile.postcode },
                { label: "Employer", value: profile.employer },
                { label: "Address", value: profile.address },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                >
                  <Typography sx={{ fontWeight: "bold", minWidth: "140px" }}>
                    {item.label}:
                  </Typography>
                  <Typography sx={{ flex: 1 }}>{item.value}</Typography>
                </Box>
              ))}

              {/* ADVANCE SECTIONS */}
              {searchType === "advance" && (
                <>
                  {/* Employment History */}
                  <Box sx={{ mt: 4 }}>
                    <Box
                      sx={{
                        borderBottom: "3px solid black",
                        mb: 2,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                      >
                        EMPLOYMENT HISTORY
                      </Typography>
                    </Box>
                    {[
                      { label: "Employer", value: profile.employer },
                      {
                        label: "Position",
                        value: profile.position || "No information available.",
                      },
                      {
                        label: "Certificate No",
                        value: profile.certificate_no || "No information available.",
                      },
                      {
                        label: "Date of issue",
                        value: profile.doi || "No information available.",
                      },
                      {
                        label: "Counter Signatory",
                        value:
                          profile.countersignatory ||
                          "No information available.",
                      },
                    ].map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: "bold", minWidth: "180px" }}
                        >
                          {item.label}:
                        </Typography>
                        <Typography sx={{ flex: 1 }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Counter Signatory */}
                  <Box sx={{ mt: 4 }}>
                    <Box
                      sx={{
                        borderBottom: "3px solid black",
                        mb: 2,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                      >
                        COUNTER SIGNATORY
                      </Typography>
                    </Box>
                    {[
                      {
                        label: "Details",
                        value: profile.countersignatory_details || "No information available.",
                      },
                      {
                        label: "Registered Person",
                        value:
                          profile.registered_entity || "No information available.",
                      },
                      {
                        label: "Counter Signatory",
                        value:
                          profile.countersignatory ||
                          "No information available.",
                      },
                    ].map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: "bold", minWidth: "180px" }}
                        >
                          {item.label}:
                        </Typography>
                        <Typography sx={{ flex: 1 }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Crime Record */}
                  <Box sx={{ mt: 4 }}>
                    <Box
                      sx={{
                        borderBottom: "3px solid black",
                        mb: 2,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                      >
                        CRIMINAL RECORDS
                      </Typography>
                    </Box>
                    {[
                      {
                        label: "Police Records",
                        value: profile.police_records || "No information available.",
                      },
                      {
                        label: "Under Section",
                        value:
                          profile.section_information || "No information available.",
                      },
                      {
                        label: "Children's Barred List",
                        value:
                          profile.dbs_children_barred_list ||
                          "No information available.",
                      },
                      {
                        label: "Adults' barred list",
                        value:
                          profile.dbs_adults_barred_list ||
                          "No information available.",
                      },
                      
                    ].map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: "bold", minWidth: "180px" }}
                        >
                          {item.label}:
                        </Typography>
                        <Typography sx={{ flex: 1 }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          
        </Paper>
      </Box>
      <Footer />
    </>
  );
};

export default ProfileDetailPage;
